import {Booking} from '../api-types/bovio/response/booking'
import {Status} from '../api-types/bovio/response/booking_status'
import {Offer} from '../offer/types/offer'
import {addDaysToDate} from '../utils/date'
import {
  BookingStatusErrorResponse,
  BookingStatusThreeDomainSecureRequiredResponse
} from './types/BookingStatus'

import type {OfferCheckAPIResponse} from '../offer/types/OfferCheck'

/**
 * Checks if the given argument is valid booking data
 * @param bookingData - The booking data to check
 * @returns `true` if `bookingData` is a valid booking
 */
export const isExistingBooking = (
  bookingData?: OfferCheckAPIResponse | Offer | Booking
): bookingData is Booking => {
  return !!(bookingData as Booking)?.confirmationId
}

interface BookAgainDates {
  bookAgainCheckIn: string
  bookAgainCheckOut: string
}

/**
 * Returns a consequent check in and check out date when provided the original checkout date
 * ie.: 2022-07-23
 * @param checkOutDate - The string that represents a date when a stay ends
 * @returns -
 */
export const getBookAgainCheckInCheckOutDates = (
  checkOutDate: string
): BookAgainDates => {
  const bookAgainCheckIn = checkOutDate
  const bookAgainCheckOut = addDaysToDate(bookAgainCheckIn, 1)
  return {bookAgainCheckIn, bookAgainCheckOut}
}

// if country is in member of European Union
const checkIsInEU = (countryCode: string) =>
  [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE'
  ].includes(countryCode)

/**
 * Checks if an international transaction fee should be applied based on the user's country and currency.
 * @param userCountry - The country of the user
 * @param userCurrency - The currency of the user
 * @returns `true` if an international transaction fee should be applied, `false` otherwise.
 */
export const checkInternationalTransactionFee = (
  userCountry?: string,
  userCurrency?: string
) => {
  const isEU = !!userCountry && checkIsInEU(userCountry)
  const isEuChargeable =
    !!userCurrency && ['EUR', 'GBP', 'USD', 'AUD', 'CAD'].includes(userCurrency)
  if (isEU && isEuChargeable) {
    return false
  }
  if (userCountry === 'US' && userCurrency === 'USD') return false
  if (userCountry === 'GB' && userCurrency === 'GBP') return false

  return true
}

/**
 * Type guard to check if booking data requires 3D Secure authentication
 * @param bookingData - The booking data to check
 * @returns True if booking requires 3DS authentication
 */
export const isBookingStatus3DSRequired = (
  bookingData: unknown
): bookingData is BookingStatusThreeDomainSecureRequiredResponse => {
  if (typeof bookingData !== 'object' || !bookingData) return false
  const data = bookingData as Record<string, unknown>
  return data.status === Status.ThreeDomainSecureRequired
}

/**
 * Type guard to check if booking data is an error response
 * @param bookingData - The booking data to check
 */
export const isBookingStatusErrorResponse = (
  bookingData: unknown
): bookingData is BookingStatusErrorResponse => {
  if (typeof bookingData !== 'object' || !bookingData) return false
  const data = bookingData as Record<string, unknown>
  return 'error' in data
}
